import ListWrapper from "../utils/ListWrapper";
import D3EFeature from "./D3EFeature";
import DFile from "../classes/DFile";
import DBObject from "../utils/DBObject";
import CloneContext from "../utils/CloneContext";
import CollectionUtils from "../utils/CollectionUtils";

export default class D3EAppTemplate extends DBObject {
  private static readonly _BACKGROUD: number = 0;
  private static readonly _DESCRIPTION: number = 1;
  private static readonly _FEATURES: number = 2;
  private static readonly _ICON: number = 3;
  private static readonly _TITLE: number = 4;
  private static readonly _TOPFEATURES: number = 5;
  public id: number = 0;
  public otherMaster: DBObject;
  private _title: string = "";
  private _topFeatures: string = "";
  private _description: string = "";
  private _features: Array<D3EFeature> = ListWrapper.reference(
    this,
    "features",
    D3EAppTemplate._FEATURES
  );
  private _backgroud: DFile = null;
  private _icon: DFile = null;
  public constructor(
    d3eParams?: Partial<{
      backgroud: DFile;
      description: string;
      features: Array<D3EFeature>;
      icon: DFile;
      title: string;
      topFeatures: string;
    }>
  ) {
    super();

    this.setBackgroud(d3eParams?.backgroud ?? null);

    this.setDescription(d3eParams?.description ?? "");

    this.setFeatures(
      d3eParams?.features ??
        ListWrapper.reference(this, "features", D3EAppTemplate._FEATURES)
    );

    this.setIcon(d3eParams?.icon ?? null);

    this.setTitle(d3eParams?.title ?? "");

    this.setTopFeatures(d3eParams?.topFeatures ?? "");
  }
  public get d3eType(): string {
    return "D3EAppTemplate";
  }
  public clear(): void {
    this.d3eChanges.clear();
  }
  public get title(): string {
    return this._title;
  }
  public setTitle(val: string): void {
    let isValChanged: boolean = this._title !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(D3EAppTemplate._TITLE, this._title);

    this._title = val;

    this.fire("title", this);
  }
  public get topFeatures(): string {
    return this._topFeatures;
  }
  public setTopFeatures(val: string): void {
    let isValChanged: boolean = this._topFeatures !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(D3EAppTemplate._TOPFEATURES, this._topFeatures);

    this._topFeatures = val;

    this.fire("topFeatures", this);
  }
  public get description(): string {
    return this._description;
  }
  public setDescription(val: string): void {
    let isValChanged: boolean = this._description !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(D3EAppTemplate._DESCRIPTION, this._description);

    this._description = val;

    this.fire("description", this);
  }
  public get features(): Array<D3EFeature> {
    return this._features;
  }
  public setFeatures(val: Array<D3EFeature>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this._features,
      val
    );

    if (!isValChanged) {
      return;
    }

    if (!this.d3eChanges.contains(D3EAppTemplate._FEATURES)) {
      let _old: Array<D3EFeature> = Array.from(this._features);

      this.updateD3EChanges(D3EAppTemplate._FEATURES, _old);
    }

    this.updateObservableColl("features", this._features, val);

    this._features.clear();

    this._features.addAll(val);

    this.fire("features", this);
  }
  public addToFeatures(val: D3EFeature, index: number = -1): void {
    let _old: Array<D3EFeature> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      D3EAppTemplate._FEATURES
    );

    if (_isNewChange) {
      _old = Array.from(this._features);
    }

    if (index === -1) {
      if (!this._features.contains(val)) this._features.add(val);
    } else {
      this._features.remove(this._features.elementAt(index));

      this._features.add(val);
    }

    this.fire("features", this, val, true);

    this.updateObservable("features", null, val);

    if (_isNewChange) {
      this.updateD3EChanges(D3EAppTemplate._FEATURES, _old);
    }
  }
  public removeFromFeatures(val: D3EFeature): void {
    let _old: Array<D3EFeature> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      D3EAppTemplate._FEATURES
    );

    if (_isNewChange) {
      _old = Array.from(this._features);
    }

    this._features.remove(val);

    this.fire("features", this, val, false);

    if (_isNewChange) {
      this.updateD3EChanges(D3EAppTemplate._FEATURES, _old);
    }
  }
  public get backgroud(): DFile {
    return this._backgroud;
  }
  public setBackgroud(val: DFile): void {
    let isValChanged: boolean = this._backgroud !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(D3EAppTemplate._BACKGROUD, this._backgroud);

    this._backgroud = val;

    this.fire("backgroud", this);
  }
  public get icon(): DFile {
    return this._icon;
  }
  public setIcon(val: DFile): void {
    let isValChanged: boolean = this._icon !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(D3EAppTemplate._ICON, this._icon);

    this._icon = val;

    this.fire("icon", this);
  }
  public get(field: number): any {
    switch (field) {
      case D3EAppTemplate._TITLE: {
        return this._title;
      }

      case D3EAppTemplate._TOPFEATURES: {
        return this._topFeatures;
      }

      case D3EAppTemplate._DESCRIPTION: {
        return this._description;
      }

      case D3EAppTemplate._FEATURES: {
        return this._features;
      }

      case D3EAppTemplate._BACKGROUD: {
        return this._backgroud;
      }

      case D3EAppTemplate._ICON: {
        return this._icon;
      }
      default: {
        return null;
      }
    }
  }
  public updateD3EChanges(index: number, value: any): void {
    if (this.lockedChanges()) {
      return;
    }

    super.updateD3EChanges(index, value);
  }
  public restore(): void {
    /*
TODO: Might be removed
*/

    this.d3eChanges.restore(this);
  }
  public deepClone(clearId = true): D3EAppTemplate {
    let ctx: CloneContext = new CloneContext({ "clearId": clearId });

    return ctx.startClone(this);
  }
  public collectChildValues(ctx: CloneContext): void {}
  public deepCloneIntoObj(dbObj: DBObject, ctx: CloneContext): void {
    let obj: D3EAppTemplate = dbObj as D3EAppTemplate;

    obj.id = this.id;

    obj.setTitle(this._title);

    obj.setTopFeatures(this._topFeatures);

    obj.setDescription(this._description);

    obj.setFeatures(this._features);

    obj.setBackgroud(this._backgroud);

    obj.setIcon(this._icon);
  }
  public set(field: number, value: any): void {
    switch (field) {
      case D3EAppTemplate._TITLE: {
        this.setTitle(value as string);
        break;
      }

      case D3EAppTemplate._TOPFEATURES: {
        this.setTopFeatures(value as string);
        break;
      }

      case D3EAppTemplate._DESCRIPTION: {
        this.setDescription(value as string);
        break;
      }

      case D3EAppTemplate._FEATURES: {
        this.setFeatures((value as Array<any>).cast<D3EFeature>().toList());
        break;
      }

      case D3EAppTemplate._BACKGROUD: {
        this.setBackgroud(value as DFile);
        break;
      }

      case D3EAppTemplate._ICON: {
        this.setIcon(value as DFile);
        break;
      }
    }
  }
}
